class BlogFilters extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onBodyClick = this.onBodyClick.bind(this);
    this.init();
  }

  init() {
    this.blogUrl = this.dataset.blogUrl;
    this.initialFilters = this.getSelectedFiltersByGroup();
    this.initialTags = this.flattenFilters(this.initialFilters);
    this.isFetching = false;

    this.trigger = document.querySelector('[data-button-filter-trigger]');
    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.toggleDrawer());
    }

    this.addEventListener('click', this.onClick, true);
    document.body.addEventListener('click', this.onBodyClick);

    const filtersFromUrl = this.getFiltersFromUrl();
    if (Object.keys(filtersFromUrl).length > 0) {
      this.applySelectedFilters(filtersFromUrl);
      this.initialFilters = filtersFromUrl;
      this.initialTags = this.flattenFilters(filtersFromUrl);

      if (this.requiresOrMode(filtersFromUrl)) {
        this.fetchSection();
      }
      return;
    }

    if (this.requiresOrMode(this.initialFilters)) {
      this.fetchSection();
    }
  }

  onClick(event) {
    const clearSummaryElement = event.target.closest('[data-filter-clear], [data-filter-summary-clear]');
    if (clearSummaryElement) {
      event.stopPropagation();
    }

    const closeBtn = event.target.closest('[data-button-filter-close]');
    if (closeBtn) return this.closeDrawer();

    const applyBtn = event.target.closest('[data-button-filter-apply]');
    if (applyBtn) return this.handleApply();

    const clearBtn = event.target.closest('[data-filter-clear]');
    if (clearBtn) return this.onClearClick(event, clearBtn);

    const tagItem = event.target.closest('[data-filter-tag-item]');
    if (tagItem) return this.onTagClick(event, tagItem);

    const summary = event.target.closest('summary');
    if (summary) this.onSummaryClick(event, summary);
  }

  toggleDrawer() {
    const isOpen = this.classList.toggle('is-open');
    document.body.classList.toggle('overflow-hidden', isOpen);
  }

  closeDrawer() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
  }

  onSummaryClick(event, summary) {
    const details = summary.parentElement;
    const isOpen = details.hasAttribute('open');

    if (window.innerWidth >= 750 && !isOpen) {
      this.closeAllExcept(details);
    }
  }

  onTagClick(event, item) {
    if (this.isFetching) return;
    event.preventDefault();

    const checkbox = item.querySelector('input[type="checkbox"]');
    checkbox.checked = !checkbox.checked;
    this.updateTagItemState(item, checkbox.checked);

    this.syncSummaryState(item.closest('details'));

    if (window.innerWidth >= 750 && this.tagsHaveChanged(this.initialTags, this.getSelectedTags())) {
      this.fetchSection();
    }
  }

  onClearClick(event, clearBtn) {
    if (this.isFetching) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (window.innerWidth >= 750) {
      event.preventDefault();
      event.stopPropagation();

      const details = clearBtn.closest('details');
      if (details) {
        details.querySelectorAll('[data-filter-tag-item]').forEach(item => {
          const checkbox = item.querySelector('input[type="checkbox"]');
          if (checkbox) checkbox.checked = false;
          this.updateTagItemState(item, false);
        });

        this.syncSummaryState(details);
      }

      this.fetchSection();
    }
  }

  setFiltersDisabled(disabled) {
    this.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.disabled = disabled);
    this.querySelectorAll('[data-filter-tag-item]').forEach(item => {
      item.style.pointerEvents = disabled ? 'none' : '';
    });
  }

  updateTagItemState(item, isChecked) {
    if (!item) return;

    const checkmark = item.querySelector('[data-filter-item-icon]');
    const checkboxVisual = item.querySelector('[data-filter-item-checkbox]');
    const listItem = item.closest('[data-filter-item]');

    checkmark?.classList.toggle('hidden', !isChecked);
    checkboxVisual?.classList.toggle('bg-primary-dark', isChecked);
    listItem?.classList.toggle('is-active', isChecked);
  }

  fetchSection() {
    this.isFetching = true;
    const filters = this.getSelectedFiltersByGroup();
    const tags = this.flattenFilters(filters);
    const useOrMode = this.requiresOrMode(filters);
    const sectionElement = this.closest('.shopify-section');
    const sectionId = sectionElement ? sectionElement.id.replace('shopify-section-', '') : 'main-blog';

    this.setFiltersDisabled(true);

    const openDetailsIndex = Array.from(this.querySelectorAll('details')).findIndex(d => d.hasAttribute('open'));
    const articlesWrapper = document.getElementById('BlogArticles');
    
    if (articlesWrapper) articlesWrapper.style.opacity = '0.5';

    const request = useOrMode
      ? this.fetchOrSections(filters, sectionId, articlesWrapper)
      : this.fetchNativeSection(tags, sectionId, articlesWrapper, openDetailsIndex);

    request
      .then(url => {
        window.history.pushState({ path: url }, '', url);
        this.initialFilters = filters;
        this.initialTags = tags;
      })
      .catch(console.error)
      .finally(() => {
        this.isFetching = false;
        this.setFiltersDisabled(false);
        const currentArticles = document.getElementById('BlogArticles');
        if (currentArticles) currentArticles.style.opacity = '1';
      });
  }

  onBodyClick(event) {
    if (window.innerWidth >= 750 && !this.contains(event.target)) {
      if (Array.from(this.querySelectorAll('details')).some(details => details.hasAttribute('open'))) {
        this.closeAll();
      }
    }
  }

  getSelectedTags() {
    return Array.from(this.querySelectorAll('input[type="checkbox"]:checked')).map(input => input.value);
  }

  getSelectedFiltersByGroup() {
    return Array.from(this.querySelectorAll('input[type="checkbox"]:checked')).reduce((groups, input) => {
      const groupId = input.dataset.filterGroup;
      if (!groupId) return groups;

      if (!groups[groupId]) groups[groupId] = [];
      groups[groupId].push(input.value);
      return groups;
    }, {});
  }

  flattenFilters(filters) {
    return Object.values(filters).flat();
  }

  requiresOrMode(filters) {
    return this.flattenFilters(filters).length > 1;
  }

  getFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const filters = {};

    params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;

      const groupId = key.replace('filter.', '');
      const tags = value.split(',').map(tag => tag.trim()).filter(Boolean);

      if (tags.length > 0) filters[groupId] = tags;
    });

    return filters;
  }

  buildUrl(filters) {
    const activeGroups = Object.entries(filters).filter(([, tags]) => tags.length > 0);
    if (activeGroups.length === 0) return this.blogUrl;

    const tags = activeGroups.flatMap(([, tags]) => tags);
    return `${this.blogUrl}/tagged/${tags.join('+')}`;
  }

  applySelectedFilters(filters) {
    const selectedValues = new Set(
      Object.entries(filters).flatMap(([groupId, tags]) => tags.map(tag => `${groupId}:${tag}`))
    );

    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      const groupId = checkbox.dataset.filterGroup;
      const isChecked = selectedValues.has(`${groupId}:${checkbox.value}`);

      checkbox.checked = isChecked;
      this.updateTagItemState(checkbox.closest('[data-filter-tag-item]'), isChecked);
    });

    this.querySelectorAll('details[data-filter-group]').forEach(details => this.syncSummaryState(details));
  }

  syncSummaryState(details) {
    if (!details) return;

    const summary = details.querySelector('[data-filter-summary]');
    const summaryTags = details.querySelector('[data-filter-summary-tags]');
    const clearButton = details.querySelector('[data-filter-summary-clear]');
    if (!summary || !summaryTags) return;

    const activeLabels = Array.from(details.querySelectorAll('input[type="checkbox"]:checked')).map(input => {
      return input.closest('[data-filter-tag-item]')?.querySelector('span:last-child')?.textContent?.trim();
    }).filter(Boolean);

    summaryTags.innerHTML = activeLabels.map(label => {
      return `<span class="text-primary-lighter underline underline-offset-4 capitalize text-1,75 decoration-black-opacity-20">${label}</span>`;
    }).join('');

    summaryTags.classList.toggle('hidden', activeLabels.length === 0);
    summary.classList.toggle('border-primary-lighter', activeLabels.length > 0);
    summary.classList.toggle('border-primary-darker', activeLabels.length === 0);

    if (clearButton) {
      const currentGroupId = details.dataset.filterGroup;
      const otherTags = this.flattenFilters(this.getSelectedFiltersByGroup()).filter(tag => {
        const currentGroupMatch = details.querySelector(
          `input[type="checkbox"][data-filter-group="${currentGroupId}"][value="${tag}"]:checked`
        );
        return !currentGroupMatch;
      });
      const shouldRotateClearButton = window.innerWidth >= 990
        ? activeLabels.length === 0
        : details.hasAttribute('open');

      clearButton.classList.toggle('rotate-45deg', shouldRotateClearButton);
      clearButton.setAttribute(
        'href',
        otherTags.length > 0 ? `${this.blogUrl}/tagged/${otherTags.join('+')}` : this.blogUrl
      );
    }
  }

  fetchNativeSection(tags, sectionId, articlesWrapper, openDetailsIndex) {
    let url = this.blogUrl;
    if (tags.length > 0) {
      url += `/tagged/${tags.join('+')}`;
    }

    const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}`;

    return fetch(fetchUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const newFilters = html.querySelector('blog-filters');
        if (newFilters) {
          this.innerHTML = newFilters.innerHTML;

          const detailsElements = this.querySelectorAll('details');
          if (openDetailsIndex > -1 && detailsElements[openDetailsIndex]) {
            detailsElements[openDetailsIndex].setAttribute('open', '');
          }
        }

        const newArticles = html.getElementById('BlogArticles');
        if (newArticles && articlesWrapper) {
          articlesWrapper.innerHTML = newArticles.innerHTML;
        }

        return url;
      });
  }

  fetchOrSections(filters, sectionId, articlesWrapper) {
    const tags = [...new Set(this.flattenFilters(filters))];
    const requests = tags.map(tag => {
      const url = `${this.blogUrl}/tagged/${tag}`;
      const fetchUrl = `${url}?section_id=${sectionId}`;

      return fetch(fetchUrl)
        .then(response => response.text())
        .then(responseText => new DOMParser().parseFromString(responseText, 'text/html'));
    });

    return Promise.all(requests).then(documents => {
      if (!articlesWrapper || documents.length === 0) return this.buildUrl(filters);

      const articleMap = new Map();
      let templateArticles = null;

      documents.forEach(doc => {
        const newArticles = doc.getElementById('BlogArticles');
        if (!newArticles) return;

        if (!templateArticles) templateArticles = newArticles;

        newArticles.querySelectorAll('.blog-articles__article').forEach(article => {
          const articleUrl = article.querySelector('a[href]')?.getAttribute('href') || article.textContent.trim();
          if (!articleMap.has(articleUrl)) {
            articleMap.set(articleUrl, article.outerHTML);
          }
        });
      });

      if (!templateArticles) return this.buildUrl(filters);

      articlesWrapper.innerHTML = templateArticles.innerHTML;

      const articlesGrid = articlesWrapper.querySelector('.d-grid');
      if (articlesGrid) {
        articlesGrid.innerHTML = Array.from(articleMap.values()).join('');

        if (articleMap.size === 0) {
          articlesGrid.innerHTML = '<p class="col-span-full text-center">No articles found.</p>';
        }
      }

      articlesWrapper.querySelector('.pagination-wrapper')?.remove();

      return this.buildUrl(filters);
    });
  }

  tagsHaveChanged(tags1, tags2) {
    if (tags1.length !== tags2.length) return true;
    const sorted1 = [...tags1].sort();
    const sorted2 = [...tags2].sort();
    return sorted1.some((val, index) => val !== sorted2[index]);
  }

  handleApply() {
    this.closeDrawer();
    this.fetchSection();
  }

  closeAllExcept(exception) {
    this.querySelectorAll('details').forEach(details => {
      if (details !== exception) details.removeAttribute('open');
    });
  }

  closeAll() {
    this.querySelectorAll('details').forEach(details => details.removeAttribute('open'));
  }
}

if (!customElements.get('blog-filters')) {
  customElements.define('blog-filters', BlogFilters);
}
